const CONVERT_VISITOR_CONTEXT_MAX_BYTES = 32 * 1024
const CONVERT_VISITOR_CONTEXT_KEYS = [
  "visitorId",
  "visitor_id",
  "bucketing",
  "segments",
]
const CONVERT_GOAL_CONTEXT_KEYS = ["key", "name", "type"]

const presentString = (value) => {
  if (value === null || value === undefined || value === "") {
    return null
  }

  return String(value)
}

const firstPresentString = (...values) =>
  values.map(presentString).find((value) => value)

const normalizedGoalText = (value) => presentString(value)?.trim().toLowerCase()

const getConvertCheckoutTrackingConfig = () =>
  window.sl?.config?.convert_checkout_tracking || {}

const getConfiguredConvertGoals = () => {
  const goals = getConvertCheckoutTrackingConfig().goals
  return Array.isArray(goals) ? goals : []
}

const getSerializedByteSize = (value) => {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(value).length
  }

  if (typeof Blob !== "undefined") {
    return new Blob([value]).size
  }

  return value.length
}

const getConvertGoalContext = (goal) => {
  if (!goal || typeof goal !== "object") {
    return null
  }

  const goalId = presentString(goal.id)
  if (!goalId) {
    return null
  }

  const goalContext = { id: goalId }
  CONVERT_GOAL_CONTEXT_KEYS.forEach((key) => {
    const goalValue = presentString(goal[key])
    if (goalValue) {
      goalContext[key] = goalValue
    }
  })

  return goalContext
}

const isRelevantConvertGoal = (goal) => {
  const goalId = presentString(goal.id)
  const goalKey = normalizedGoalText(goal.key)
  const goalName = normalizedGoalText(goal.name)
  const configuredGoals = getConfiguredConvertGoals()
  const relevantGoalIds = []
  const relevantGoalKeys = []
  const relevantGoalNames = []

  configuredGoals.forEach((configuredGoal) => {
    const configuredGoalId = presentString(configuredGoal?.id)
    if (configuredGoalId) {
      relevantGoalIds.push(configuredGoalId)
    }

    const configuredGoalKey = normalizedGoalText(configuredGoal?.key)
    if (configuredGoalKey) {
      relevantGoalKeys.push(configuredGoalKey)
    }

    const keyAliases = Array.isArray(configuredGoal?.aliases?.keys)
      ? configuredGoal.aliases.keys
      : []

    keyAliases
      .map(normalizedGoalText)
      .filter(Boolean)
      .forEach((keyAlias) => relevantGoalKeys.push(keyAlias))

    const configuredGoalName = normalizedGoalText(configuredGoal?.name)
    if (configuredGoalName) {
      relevantGoalNames.push(configuredGoalName)
    }
  })

  return (
    relevantGoalIds.includes(goalId) ||
    relevantGoalKeys.includes(goalKey) ||
    relevantGoalNames.includes(goalName)
  )
}

const getTargetConvertGoals = () => getConfiguredConvertGoals()

const mergeConvertGoalContext = (existingGoal, nextGoal) => {
  const mergedGoal = {
    id: firstPresentString(existingGoal.id, nextGoal.id),
  }

  CONVERT_GOAL_CONTEXT_KEYS.forEach((key) => {
    const goalValue = firstPresentString(existingGoal[key], nextGoal[key])
    if (goalValue) {
      mergedGoal[key] = goalValue
    }
  })

  return mergedGoal
}

const mergeConvertGoals = (...goalSets) => {
  const goalPositions = new Map()
  const goals = []

  goalSets
    .reduce((allGoals, goalSet) => allGoals.concat(goalSet), [])
    .forEach((goal) => {
      const normalizedGoal = getConvertGoalContext(goal)
      if (!normalizedGoal) {
        return
      }

      const existingGoalIndex = goalPositions.get(normalizedGoal.id)
      if (existingGoalIndex !== undefined) {
        goals[existingGoalIndex] = mergeConvertGoalContext(
          goals[existingGoalIndex],
          normalizedGoal
        )
        return
      }

      goalPositions.set(normalizedGoal.id, goals.length)
      goals.push(normalizedGoal)
    })

  return goals
}

const getConvertDataContext = () => {
  const convertData = window.convert?.data
  if (!convertData || typeof convertData !== "object") {
    return {}
  }

  const project =
    convertData.project && typeof convertData.project === "object"
      ? convertData.project
      : {}
  const accountId = firstPresentString(
    convertData.account_id,
    convertData.accountId
  )
  const projectId = firstPresentString(
    convertData.project_id,
    convertData.projectId,
    project.id
  )
  const goals = Array.isArray(convertData.goals)
    ? convertData.goals
        .map(getConvertGoalContext)
        .filter(Boolean)
        .filter(isRelevantConvertGoal)
    : []
  const targetGoals = getTargetConvertGoals()
  const mergedGoals = mergeConvertGoals(goals, targetGoals)
  const context = {}

  if (accountId) {
    context.account_id = accountId
  }
  if (projectId) {
    context.project_id = projectId
  }
  if (mergedGoals.length) {
    context.goals = mergedGoals
  }

  return context
}

export const getConvertVisitorContext = () => {
  if (typeof window === "undefined") {
    return null
  }

  const getCurrentVisitorData = window.convert?.getCurrentVisitorData
  if (typeof getCurrentVisitorData !== "function") {
    return null
  }

  try {
    const visitorData = getCurrentVisitorData.call(window.convert)
    if (!visitorData || typeof visitorData !== "object") {
      return null
    }

    const visitorContext = {}
    CONVERT_VISITOR_CONTEXT_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(visitorData, key)) {
        visitorContext[key] = visitorData[key]
      }
    })

    if (!Object.keys(visitorContext).length) {
      return null
    }

    Object.assign(visitorContext, getConvertDataContext())

    const serializedVisitorData = JSON.stringify(visitorContext)
    if (
      !serializedVisitorData ||
      getSerializedByteSize(serializedVisitorData) >
        CONVERT_VISITOR_CONTEXT_MAX_BYTES
    ) {
      return null
    }

    return JSON.parse(serializedVisitorData)
  } catch (_e) {
    return null
  }
}
